import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

// Load Stripe with error handling - prevents app crash when Stripe.js fails to load (network, ad blocker, etc.)
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey).catch((err) => {
      console.warn('Stripe failed to load. Payment may be unavailable.', err?.message || err);
      return null;
    })
  : null;

const elementOptions = {
  style: {
    base: {
      fontSize: '14px',
      color: '#2c2c2c',
      fontFamily:
        "'Be Vietnam Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      '::placeholder': {
        color: '#9aa0a6',
        fontFamily: "'Be Vietnam Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      },
    },
    invalid: { color: '#dc3545' },
  },
};

function RentPaymentForm({
  amountCents = 0,
  clientSecret,
  propertyAddress,
  onPaymentSuccess,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);

  const amountText = useMemo(() => `$${(amountCents / 100).toFixed(2)}`, [amountCents]);

  // Browser autofill fills the whole card in one shot, so the field jumps from
  // empty straight to complete in a single change event. Moving focus in the
  // middle of that cancels the fill and leaves the card input stuck - it looks
  // disabled and stops accepting typing. Typing produces a change per
  // keystroke, so only auto-advance once we have seen more than one change,
  // and never onto a field autofill has already filled.
  const numberEdits = useRef(0);
  const expiryEdits = useRef(0);

  const handleCardNumberChange = (event) => {
    numberEdits.current = event.empty ? 0 : numberEdits.current + 1;
    if (!event.complete || numberEdits.current < 2 || !elements) return;
    const expiryElement = elements.getElement(CardExpiryElement);
    if (expiryElement) setTimeout(() => expiryElement.focus(), 50);
  };

  const handleExpiryChange = (event) => {
    expiryEdits.current = event.empty ? 0 : expiryEdits.current + 1;
    if (!event.complete || expiryEdits.current < 2 || !elements) return;
    const cvcElement = elements.getElement(CardCvcElement);
    if (cvcElement) setTimeout(() => cvcElement.focus(), 50);
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!clientSecret) {
      toast.error('Payment is not ready yet. Please wait a moment and try again.');
      return;
    }
    if (!stripe || !elements) return;

    setIsLoading(true);
    try {
      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) {
        toast.error('Card element not ready. Please try again.');
        return;
      }

      // A rent subscription can come back as either a first invoice payment (pi_...)
      // or as a card-saving setup intent (seti_...) when the first charge is scheduled later.
      const isSetupIntent = String(clientSecret).startsWith('seti_');

      const { error, paymentIntent, setupIntent } = isSetupIntent
        ? await stripe.confirmCardSetup(clientSecret, {
            payment_method: { card: cardNumber },
          })
        : await stripe.confirmCardPayment(clientSecret, {
            payment_method: { card: cardNumber },
          });

      if (error) {
        toast.error(error.message || 'Payment failed. Please try again.');
        return;
      }

      const intent = paymentIntent || setupIntent;
      if (intent?.status === 'succeeded') {
        toast.success('Payment successful');
        onPaymentSuccess?.();
      } else {
        toast.info(`Payment status: ${intent?.status || 'unknown'}`);
      }
    } catch (err) {
      toast.error(err?.message || 'Payment failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="tenant-screening-payment-form">
      <div className="tenant-screening-payment-header">
        <div className="tenant-screening-payment-icon-wrap">
          <svg className="tenant-screening-payment-wallet-icon" width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="72" height="72" rx="14" fill="#43BF67" fillOpacity="0.34"/>
            <path d="M22 26h28a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H22a2 2 0 0 1-2-2V28a2 2 0 0 1 2-2z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M26 26V22a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="38" y="36" width="8" height="4" rx="1" fill="white"/>
          </svg>
        </div>
        <h1 className="tenant-screening-payment-title">Complete Payment</h1>
        <p className="tenant-screening-payment-subtitle">Secure payment for your monthly rent</p>
      </div>

      <div className="payment-summary-card tenant-screening-summary-card">
        <div className="payment-summary-row">
          <div className="payment-summary-label">Monthly Rent</div>
          <div className="payment-summary-price">{amountText}</div>
        </div>
        <div className="payment-summary-divider" />
        <div className="payment-summary-row total">
          <div className="payment-summary-label">Total</div>
          <div className="payment-summary-total">{amountText}</div>
        </div>
        <div className="payment-summary-footnote">
          {amountText} will be charged every month for {propertyAddress || 'this property'}.
        </div>
      </div>

      <form onSubmit={handlePay} className="payment-card-form tenant-screening-card-form" noValidate autoComplete="off" data-form-type="other">
        <div className="payment-field">
          <label className="payment-label">Card Number</label>
          <div className="payment-stripe-input payment-stripe-input-with-icon" data-autocomplete="off">
            <svg className="payment-card-icon" width="20" height="16" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="0.5" y="3.5" width="23" height="12" rx="1.5" stroke="#9aa0a6" strokeWidth="1"/>
              <rect x="2" y="6" width="20" height="2" rx="1" fill="#9aa0a6"/>
              <rect x="2" y="10" width="8" height="2" rx="1" fill="#9aa0a6"/>
            </svg>
            <CardNumberElement
              options={{ ...elementOptions, disableLink: true }}
              onChange={handleCardNumberChange}
            />
          </div>
        </div>

        <div className="payment-row">
          <div className="payment-field">
            <label className="payment-label">Expiry</label>
            <div className="payment-stripe-input" data-autocomplete="off">
              <CardExpiryElement
                options={{ ...elementOptions, disableLink: true }}
                onChange={handleExpiryChange}
              />
            </div>
          </div>
          <div className="payment-field">
            <label className="payment-label">CVC</label>
            <div className="payment-stripe-input" data-autocomplete="off">
              <CardCvcElement options={{ ...elementOptions, disableLink: true }} />
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="payment-submit-button tenant-screening-pay-button"
          disabled={isLoading || !clientSecret}
        >
          {isLoading ? 'Processing...' : `Pay ${amountText}`}
        </button>
      </form>
    </div>
  );
}

const RentSubscriptionPayment = () => {
  const { id: propertyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const propertyAddress = location.state?.propertyAddress || 'this property';
  const propertyCode = location.state?.propertyCode || '';
  const initialClientSecret = location.state?.clientSecret || '';
  const initialRentAmount = location.state?.rentAmount;

  const [isInitLoading, setIsInitLoading] = useState(!initialClientSecret);
  const [initError, setInitError] = useState('');
  const [clientSecret, setClientSecret] = useState(initialClientSecret);
  const [rentAmount, setRentAmount] = useState(
    initialRentAmount != null ? Number(initialRentAmount) : null
  );
  const [showSuccess, setShowSuccess] = useState(false);

  // The subscribe call normally happens on the property page and hands us the
  // client_secret through navigation state. On a direct visit / refresh we ask for it again.
  useEffect(() => {
    if (initialClientSecret) return;

    let cancelled = false;

    const initPayment = async () => {
      setIsInitLoading(true);
      setInitError('');
      try {
        const response = await postApi('/rent/subscribe', { property_id: propertyId });
        if (cancelled) return;

        const data = response?.data || {};
        const cs = data.client_secret || data.clientSecret || '';

        if (!cs) {
          throw new Error(response?.message || 'Missing client_secret from rent subscribe');
        }

        setClientSecret(cs);
        if (data.rent_amount != null) setRentAmount(Number(data.rent_amount));
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.message || e?.message || 'Failed to start the rent payment';
          setInitError(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setIsInitLoading(false);
      }
    };

    initPayment();
    return () => { cancelled = true; };
  }, [propertyId, initialClientSecret]);

  const amountCents = useMemo(() => {
    const price = Number(rentAmount);
    if (!Number.isFinite(price)) return 0;
    return Math.round(price * 100);
  }, [rentAmount]);

  const handleBackToProperty = () => {
    navigate(`/tenant/properties/${propertyId}`, { replace: true });
  };

  if (showSuccess) {
    return (
      <div className="tenant-screening-payment-page tenant-screening-success-page">
        <div className="tenant-screening-success-top">
          <div className="tenant-screening-success-icon-wrap">
            <img
              src="/assets/images/paySuccess.svg"
              alt=""
              className="tenant-screening-success-icon"
            />
          </div>
          <h1 className="tenant-screening-success-title">Payment successful</h1>
          <p className="tenant-screening-success-message">
            Thankyou for your payment.
          </p>
        </div>

        <div className="tenant-screening-success-card">
          <div className="tenant-screening-success-details">
            <h2 className="tenant-screening-success-subtitle">Monthly rent subscription active</h2>
            <p className="tenant-screening-success-text">
              Your rent will now be collected automatically every month, so you never miss a due date.
            </p>
            <div className="tenant-screening-success-meta">
              <div>
                <strong>Property Address:</strong> {propertyAddress}
              </div>
              {propertyCode && (
                <div>
                  <strong>Property Code:</strong> {propertyCode}
                </div>
              )}
              {Number.isFinite(Number(rentAmount)) && (
                <div>
                  <strong>Monthly Rent:</strong> ${Number(rentAmount).toFixed(2)}
                </div>
              )}
            </div>

            <p className="tenant-screening-success-note">
              <strong>Important:</strong> You can cancel the monthly rent subscription anytime from the
              property details page.
            </p>

            <button
              type="button"
              className="payment-submit-button tenant-screening-pay-button"
              onClick={handleBackToProperty}
            >
              Back to Property
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tenant-screening-payment-page">
      <div className="tenant-screening-payment-content">
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <RentPaymentForm
              amountCents={amountCents}
              clientSecret={clientSecret}
              propertyAddress={propertyAddress}
              onPaymentSuccess={() => setShowSuccess(true)}
            />
            {isInitLoading && (
              <div className="tenant-screening-payment-loading">
                Loading...
              </div>
            )}
            {!isInitLoading && initError && (
              <div className="tenant-screening-payment-error">
                {initError}
              </div>
            )}
          </Elements>
        ) : (
          <div className="tenant-screening-payment-form">
            <div className="tenant-screening-payment-header">
              <h1 className="tenant-screening-payment-title">Complete Payment</h1>
              <p className="tenant-screening-payment-subtitle">Secure payment for your monthly rent</p>
            </div>
            <div className="tenant-screening-payment-error">
              Stripe is not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in .env
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RentSubscriptionPayment;
